import { useTranslation } from 'react-i18next';
import {
  BookOpen, Upload, MonitorUp, ClipboardList, Image as ImageIcon,
  Volume2, MessageCircleQuestion, Clock, Download, ShieldAlert
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LegalLayout } from '../components/LegalLayout';

const CONTENT = {
  fr: {
    title: "Guide d'utilisation",
    intro: "ARIA vous accompagne dans l'analyse d'images médicales d'urgence. Ce guide présente les principales fonctionnalités, étape par étape.",
    sections: [
      {
        icon: Upload,
        title: "1. Lancer une analyse",
        items: [
          "Depuis « Nouvelle Analyse », glissez-déposez jusqu'à 5 images (JPEG, PNG, WEBP ou DICOM .dcm) ou cliquez pour parcourir.",
          "Vous pouvez aussi coller une image copiée avec Ctrl+V (Cmd+V sur Mac/iPad).",
          "Renseignez le contexte clinique (âge, sexe, symptômes, traumatisme) : plus il est précis, plus l'analyse est pertinente.",
          "Choisissez la langue du rapport (Français ou Anglais), puis cliquez sur « Analyser l'Image ».",
          "Optionnel : joignez un examen antérieur pour une analyse comparative.",
        ],
      },
      {
        icon: MonitorUp,
        title: "2. Capture d'écran",
        items: [
          "Sur ordinateur : « Capturer l'écran » ouvre le sélecteur du navigateur (écran entier, fenêtre ou onglet). Figez la vue, utilisez l'image entière ou sélectionnez une zone.",
          "Sur iPad/iPhone : la capture web n'est pas prise en charge par le navigateur. Faites une capture native (Marche + Volume haut) puis importez-la depuis Photos ou collez-la.",
        ],
      },
      {
        icon: ClipboardList,
        title: "3. Lire le rapport",
        items: [
          "Le bandeau supérieur affiche le niveau d'urgence (CRITICAL, HIGH, MODERATE, LOW) et le drapeau d'urgence radiologique (YES / NO / UNCERTAIN).",
          "Le rapport est structuré : signes critiques, complications immédiates, anomalies principales, conclusion hiérarchisée, recommandations et limites de l'analyse.",
          "Une alerte sonore et un bandeau rouge signalent les urgences vitales détectées.",
        ],
      },
      {
        icon: ImageIcon,
        title: "4. Visualiseur d'images",
        items: [
          "Zoom (molette ou boutons), déplacement (glisser), inversion des niveaux de gris, plein écran.",
          "Préréglages de fenêtrage : Défaut, Os, Poumon, Cerveau, Tissus mous — ajustez ensuite finement avec les curseurs luminosité/contraste.",
          "Les marqueurs colorés localisent les lésions détectées par l'IA (légende sur l'image).",
          "Mode annotation : dessinez un rectangle sur l'image pour ajouter vos propres repères étiquetés.",
        ],
      },
      {
        icon: Volume2,
        title: "5. Lecture vocale du rapport",
        items: [
          "Cliquez sur « Écouter » dans le bandeau du rapport pour entendre la synthèse vocale.",
          "Utilisez Pause / Reprendre à tout moment ; la lecture s'arrête automatiquement si vous quittez la page.",
        ],
      },
      {
        icon: MessageCircleQuestion,
        title: "6. Seconde lecture (questions de suivi)",
        items: [
          "Posez des questions ciblées sur l'analyse dans le panneau « Seconde lecture ».",
          "Joignez jusqu'à 4 images par question (captures, collages Ctrl+V) pour orienter la réponse de l'IA.",
        ],
      },
      {
        icon: Clock,
        title: "7. Historique et tableau de bord",
        items: [
          "L'Historique liste toutes vos analyses avec recherche, filtres (urgence, niveau, modalité, dates) et export CSV.",
          "Créez un lien de partage en lecture seule pour transmettre une analyse à un confrère.",
          "Le Tableau de Bord présente vos statistiques : volumes, répartition des urgences, modalités.",
        ],
      },
      {
        icon: Download,
        title: "8. Export PDF",
        items: [
          "Téléchargez le rapport complet en PDF (en-tête ARIA, bandeau d'urgence, sections structurées) pour vos dossiers ou transmissions.",
        ],
      },
    ],
    safety_title: "Avertissement important",
    safety: "ARIA est une aide à l'interprétation assistée par IA. Elle ne remplace en aucun cas l'évaluation d'un radiologue qualifié. En cas de suspicion d'urgence vitale, agissez immédiatement sans attendre de confirmation.",
  },
  en: {
    title: "User Guide",
    intro: "ARIA assists you with emergency medical image analysis. This guide walks you through the main features, step by step.",
    sections: [
      {
        icon: Upload,
        title: "1. Start an analysis",
        items: [
          "From \u201cNew Analysis\u201d, drag & drop up to 5 images (JPEG, PNG, WEBP or DICOM .dcm) or click to browse.",
          "You can also paste a copied image with Ctrl+V (Cmd+V on Mac/iPad).",
          "Fill in the clinical context (age, sex, symptoms, trauma): the more precise it is, the more relevant the analysis.",
          "Choose the report language (French or English), then click \u201cAnalyze Image\u201d.",
          "Optional: attach a prior exam for comparative analysis.",
        ],
      },
      {
        icon: MonitorUp,
        title: "2. Screen capture",
        items: [
          "On desktop: \u201cCapture screen\u201d opens the browser picker (entire screen, window or tab). Freeze the view, use the full image or select an area.",
          "On iPad/iPhone: web screen capture is not supported by the browser. Take a native screenshot (Power + Volume Up) then import it from Photos or paste it.",
        ],
      },
      {
        icon: ClipboardList,
        title: "3. Reading the report",
        items: [
          "The top strip shows the urgency level (CRITICAL, HIGH, MODERATE, LOW) and the radiological emergency flag (YES / NO / UNCERTAIN).",
          "The report is structured: critical findings, time-sensitive complications, key anomalies, hierarchized impression, recommendations and analysis limitations.",
          "An audible alert and a red banner signal detected life-threatening emergencies.",
        ],
      },
      {
        icon: ImageIcon,
        title: "4. Image viewer",
        items: [
          "Zoom (wheel or buttons), pan (drag), grayscale inversion, fullscreen.",
          "Windowing presets: Default, Bone, Lung, Brain, Soft tissue — then fine-tune with the brightness/contrast sliders.",
          "Colored markers locate AI-detected lesions (legend on the image).",
          "Annotation mode: draw a rectangle on the image to add your own labeled markers.",
        ],
      },
      {
        icon: Volume2,
        title: "5. Report voice playback",
        items: [
          "Click \u201cListen\u201d in the report strip to hear the spoken summary.",
          "Use Pause / Resume at any time; playback stops automatically when you leave the page.",
        ],
      },
      {
        icon: MessageCircleQuestion,
        title: "6. Second reading (follow-up questions)",
        items: [
          "Ask targeted questions about the analysis in the \u201cSecond reading\u201d panel.",
          "Attach up to 4 images per question (captures, Ctrl+V pastes) to guide the AI's answer.",
        ],
      },
      {
        icon: Clock,
        title: "7. History and dashboard",
        items: [
          "History lists all your analyses with search, filters (emergency, level, modality, dates) and CSV export.",
          "Create a read-only share link to send an analysis to a colleague.",
          "The Dashboard shows your statistics: volumes, emergency distribution, modalities.",
        ],
      },
      {
        icon: Download,
        title: "8. PDF export",
        items: [
          "Download the full report as a PDF (ARIA header, emergency banner, structured sections) for your records or referrals.",
        ],
      },
    ],
    safety_title: "Important notice",
    safety: "ARIA is an AI-assisted interpretation aid. It never replaces the assessment of a qualified radiologist. If a life-threatening emergency is suspected, act immediately without waiting for confirmation.",
  },
};

export default function GuidePage() {
  const { i18n } = useTranslation();
  const c = CONTENT[i18n.language === 'en' ? 'en' : 'fr'];

  return (
    <LegalLayout>
      <div data-testid="guide-page">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen size={26} className="text-cyan-400" />
          <h1 className="text-2xl font-bold text-foreground">{c.title}</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">{c.intro}</p>

        <div className="space-y-4">
          {c.sections.map((s, i) => {
            const Icon = s.icon;
            return (
              <Card key={i} className="bg-[hsl(210,18%,9%)] border-border" data-testid={`guide-section-${i}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2.5">
                    <Icon size={18} className="text-teal-400 shrink-0" />
                    {s.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {s.items.map((it, j) => (
                      <li key={j} className="text-sm text-foreground/80 leading-relaxed flex gap-2.5">
                        <span className="text-cyan-400 shrink-0 mt-0.5">•</span>
                        {it}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-8 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex gap-3" data-testid="guide-safety-notice">
          <ShieldAlert size={20} className="text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-300">{c.safety_title}</p>
            <p className="text-xs text-red-300/80 mt-1 leading-relaxed">{c.safety}</p>
          </div>
        </div>
      </div>
    </LegalLayout>
  );
}
